import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { pageIndicator } from "../apollo";
import { AsideAdminComponent } from "../layout/aside-admin";
import { useListStaffUser } from "../hooks/useListStaffUser";
import { useListLinkServiceSelect } from "../hooks/useListLinkServiceSelect";
import {
  EditStaffUserInput,
  EditStaffUserMutation,
  useEditStaffUser,
} from "../hooks/useEditStaffUser";
import { useDelUser } from "../hooks/useDelUser";
import { UserRole, WorkerType } from "../generated/graphql";

export interface IEditStaffUserFormProps {
  name?: string;
  phone?: string;
  password?: string;
}

// 계정 목록에는 검색/편집 대상을 id로 조회하는 별도 API가 없어서,
// listStaffUser를 넉넉한 row 수로 불러온 뒤 그 안에서 대상 계정을 찾는다.
// (관리자/직원 계정 수는 많지 않아 이 방식으로 충분하다.)
const FETCH_ALL_ROW = 500;

export const AdminUpAccount = () => {
  const params = useParams();
  const navigate = useNavigate();
  const userId = params.userId ? +params.userId : 0;

  const [role, setRole] = useState<UserRole>(UserRole.Admin);
  const [workerType, setWorkerType] = useState<WorkerType>(WorkerType.Driver);
  const [linkServiceIds, setLinkServiceIds] = useState<number[]>([]);
  const [loaded, setLoaded] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
  } = useForm<IEditStaffUserFormProps>({ mode: "all" });

  const [
    listLinkServiceSelect,
    { data: linkserviceData },
  ] = useListLinkServiceSelect();

  const [listStaffUser, { data: staffUserData }] = useListStaffUser();

  useEffect(() => {
    pageIndicator("account");
    listLinkServiceSelect();
    listStaffUser({ variables: { input: { page: 1, row: FETCH_ALL_ROW } } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loaded) return;
    const user = staffUserData?.listStaffUser.users?.find(
      (candidate) => candidate.id === userId
    );
    if (!user) return;
    setValue("name", user.name ?? "");
    setValue("phone", user.phone ?? "");
    setRole(user.role);
    if (user.workerType) setWorkerType(user.workerType);
    setLinkServiceIds(user.linkServices?.map((service) => service.id) ?? []);
    setLoaded(true);
  }, [staffUserData, loaded, userId, setValue]);

  const onEditCompleted = async (data: EditStaffUserMutation) => {
    const {
      editStaffUser: { ok, err },
    } = data;
    if (ok) {
      alert("저장되었습니다.");
      navigate("/account");
    } else {
      alert(err);
    }
  };
  const [editStaffUser, { loading }] = useEditStaffUser(onEditCompleted);

  const toggleService = (id: number) => {
    setLinkServiceIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const onSubmit = () => {
    if (loading || !userId) return;
    const { name, phone, password } = getValues();
    const input: EditStaffUserInput = {
      userId,
      name,
      phone,
      role,
      ...(role === UserRole.Worker && { workerType }),
      linkServiceIds,
      ...(password && { password }),
    };
    editStaffUser({ variables: { input } });
  };

  const onDelCompleted = async (data: any) => {
    const {
      delUser: { ok, err },
    } = data;
    if (ok) {
      alert("계정이 삭제되었습니다.");
      navigate("/account");
    } else {
      alert(err);
    }
  };
  const [delUser, { loading: delUserLoading }] = useDelUser(onDelCompleted);
  const delUserHandler = () => {
    if (delUserLoading || !userId) return;
    if (
      window.confirm(
        "삭제한 계정은 복구할 수 없습니다. 정말로 삭제하시겠습니까?"
      )
    ) {
      delUser({ variables: { input: { id: userId } } });
    }
  };

  return (
    <div className="bg-main bg-opacity-50 w-full h-full min-h-screen">
      <div className="w-full mx-auto h-full min-h-screen bg-white">
        <main id="main">
          <div className="grid grid-cols-[200px_1fr] max-w-full">
            <AsideAdminComponent />
            <div className="mypages bg-gray-100">
              <div className="orderWrap">
                <div className="bg-gray-100 h-fix-content relative">
                  <div className={`w-full h-full px-10 py-8`}>
                    <div className="grid grid-cols-[300px_1fr_200px]">
                      <div className="flex flex-wrap">
                        <div className="border-l-4 border-[#3D3E49] my-2 ml-2 w-4 h-5"></div>
                        <h1 className="inline-block font-bold mb-2 text-[24px] text-[#3D3E49]">
                          계정 편집
                        </h1>
                      </div>
                      <div className="subtitle h-full flex flex-wrap content-end"></div>
                    </div>
                    <div className="h-14 w-full max-w-[1623px] rounded-t-2xl bg-[#3D3E49] ">
                      <div className="flex flex-wrap justify-end pr-8">
                        <div className="flex flex-wrap gap-x-3">
                          <button
                            type="button"
                            onClick={delUserHandler}
                            className="bg-transparent bg-[#B42713] w-[104px] h-[30px] py-[7px] text-xs font-bold text-center rounded-lg my-[13px] text-white"
                          >
                            삭제
                          </button>
                          <button
                            form="update"
                            className="bg-transparent bg-[#8b8c8c] w-[104px] h-[30px] py-[7px] text-xs font-bold text-center rounded-lg my-[13px] text-white"
                          >
                            저장
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="shadow-lg w-full bg-white">
                      <form onSubmit={handleSubmit(onSubmit)} id="update">
                        <div className="pl-9 pr-11 py-8 space-y-4 max-w-[640px]">
                          <div className="space-y-2">
                            <p className="font-bold">아이디</p>
                            <p className="py-2">
                              {
                                staffUserData?.listStaffUser.users?.find(
                                  (candidate) => candidate.id === userId
                                )?.username
                              }
                            </p>
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold">이름</p>
                            <input
                              {...register("name")}
                              type="text"
                              className="rounded-lg border w-full py-2 px-3"
                            />
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold">연락처</p>
                            <input
                              {...register("phone")}
                              type="text"
                              className="rounded-lg border w-full py-2 px-3"
                            />
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold">
                              비밀번호 변경(선택, 비워두면 유지)
                            </p>
                            <input
                              {...register("password")}
                              type="password"
                              className="rounded-lg border w-full py-2 px-3"
                            />
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold">권한</p>
                            <div className="flex flex-wrap gap-x-6">
                              <label className="flex flex-wrap items-center gap-x-2">
                                <input
                                  type="radio"
                                  name="role"
                                  checked={role === UserRole.Admin}
                                  onChange={() => setRole(UserRole.Admin)}
                                />
                                관리자
                              </label>
                              <label className="flex flex-wrap items-center gap-x-2">
                                <input
                                  type="radio"
                                  name="role"
                                  checked={role === UserRole.Worker}
                                  onChange={() => setRole(UserRole.Worker)}
                                />
                                직원
                              </label>
                            </div>
                          </div>
                          {role === UserRole.Worker && (
                            <div className="space-y-2">
                              <p className="font-bold">직군</p>
                              <select
                                className="rounded-lg border py-2 px-3"
                                value={workerType}
                                onChange={(e) =>
                                  setWorkerType(e.target.value as WorkerType)
                                }
                              >
                                <option value={WorkerType.Driver}>기사</option>
                                <option value={WorkerType.Packer}>패커</option>
                                <option value={WorkerType.Pilot}>조종사</option>
                              </select>
                            </div>
                          )}
                          <div className="space-y-2">
                            <p className="font-bold">배정 서비스</p>
                            <p className="text-xs text-[#8B8C8C]">
                              선택한 서비스의 거래처/상품/배너/배송관리/주문관리만
                              볼 수 있습니다. 하나도 선택하지 않으면 전체
                              서비스를 볼 수 있는 수퍼관리자로 취급됩니다.
                            </p>
                            <div className="flex flex-wrap gap-x-4 gap-y-2">
                              {linkserviceData?.listLinkServiceSelect.linkservice?.map(
                                (service) => (
                                  <label
                                    key={`service-${service.id}`}
                                    className="flex flex-wrap items-center gap-x-2"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={linkServiceIds.includes(
                                        service.id
                                      )}
                                      onChange={() =>
                                        toggleService(service.id)
                                      }
                                    />
                                    {service.name}
                                  </label>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
